import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { DocumentCategory, DocumentChecklistItem, DocumentWithVersions, Loan } from '@/types';

export function useLoanDocuments(loanId: string) {
  return useQuery<DocumentWithVersions[]>({
    queryKey: ['loan-documents', loanId],
    queryFn: async () => {
      const res = await api.get(`/loans/${loanId}/document-manager`);
      return (
        res.data as Array<{
          id: string;
          name?: string;
          fileName?: string;
          category?: DocumentCategory;
          versionCount: number;
          latestVersion?: DocumentWithVersions['latestVersion'] | null;
          signatureStatus?: DocumentWithVersions['signatureStatus'];
          createdAt?: string;
          uploadedAt?: string;
          updatedAt?: string;
          lastUpdated?: string;
        }>
      ).map(doc => ({
        id: doc.id,
        name: doc.name ?? doc.fileName ?? 'Untitled document',
        category: doc.category,
        versionCount: doc.versionCount,
        latestVersion: doc.latestVersion ?? undefined,
        signatureStatus: doc.signatureStatus,
        createdAt: doc.createdAt ?? doc.uploadedAt ?? new Date(0).toISOString(),
        updatedAt:
          doc.updatedAt ??
          doc.lastUpdated ??
          doc.createdAt ??
          doc.uploadedAt ??
          new Date(0).toISOString(),
      }));
    },
    enabled: !!loanId,
  });
}

export function useDocumentTimeline(loanId: string) {
  return useQuery<{
    events: {
      timestamp: string;
      action: string;
      documentName: string;
      actor: string;
    }[];
  }>({
    queryKey: ['document-timeline', loanId],
    queryFn: async () => {
      const res = await api.get(`/loans/${loanId}/document-manager/timeline`);
      return res.data;
    },
    enabled: !!loanId,
  });
}

export function useDocumentChecklist(loanId: string) {
  return useQuery<DocumentChecklistItem[]>({
    queryKey: ['document-checklist', loanId],
    queryFn: async () => {
      const res = await api.get(`/loans/${loanId}/document-manager/checklist`);
      return res.data;
    },
    enabled: !!loanId,
  });
}

export function useDocumentMatrix(loanId: string) {
  return useQuery<{
    loanNumber?: string;
    loanType?: string;
    status?: string;
    completionPercentage: number;
    totalRequired: number;
    totalCompleted: number;
    items: Array<{
      category: DocumentCategory;
      required: boolean;
      completed: boolean;
      lastUpdated?: string;
      latestVersion?: {
        versionNumber: number;
        createdAt: string;
      } | null;
    }>;
  }>({
    queryKey: ['document-matrix', loanId],
    queryFn: async () => {
      const res = await api.get(`/loans/${loanId}/document-manager/matrix`);
      return res.data;
    },
    enabled: !!loanId,
  });
}

export function useCategoriseDocument(loanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      documentId,
      category,
    }: {
      documentId: string;
      category: DocumentCategory;
    }) => {
      const res = await api.post(`/loans/${loanId}/document-manager/${documentId}/categorise`, {
        category,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-documents', loanId] });
      queryClient.invalidateQueries({ queryKey: ['document-checklist', loanId] });
      queryClient.invalidateQueries({ queryKey: ['document-matrix', loanId] });
    },
  });
}

export function useSetLegalRef(loanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (legalMatterRef: string) => {
      const res = await api.patch(`/loans/${loanId}/document-manager/legal-ref`, {
        legalMatterRef,
      });
      return res.data;
    },
    onSuccess: loan => {
      queryClient.setQueryData<Loan | undefined>(['loans', loanId], currentLoan =>
        currentLoan
          ? { ...currentLoan, legalMatterRef: (loan as Loan).legalMatterRef }
          : currentLoan,
      );
      queryClient.invalidateQueries({ queryKey: ['loans', loanId], exact: true });
      queryClient.invalidateQueries({ queryKey: ['loan-documents', loanId] });
    },
  });
}
